import { ActionIcon, AppShell } from '@mantine/core';
import { IconHelp, IconMenu2 } from '@tabler/icons-react';
import { COMMON_DYNAMIC_ROUTES, getFullName } from '@zorro/common-utils';
import { Button } from '@zorro/design-system/v2/Button';
import { ZorroLogo } from '@zorro/shared/assets';
import {
  Center,
  Group,
  Icon,
  brand,
  useDisclosure,
} from '@zorro/web/design-system';
import {
  useDynamicRouter,
  useImpersonation,
  useMobileDetector,
} from '@zorro/web/ui-utils';
import { ReactNode } from 'react';

import { useAnalytics } from '../../Analytics';
import { showIntercom } from '../../IntercomWidget/intercom-service';
import { useAppLayout } from '../AppLayoutContext';
import { AppLayoutConstants, AppLayoutHeaderSizes } from '../AppLayoutUtils';
import classNames from './AppLayoutHeader.module.scss';
import {
  AppLayoutHeaderDiscoveryShopping,
  DiscoveryShoppingProps,
} from './AppLayoutHeaderDiscoveryShopping';
import { AppLayoutHeaderImpersonation } from './AppLayoutHeaderImpersonation';
import { AppLayoutHeaderMenu } from './AppLayoutHeaderMenu';
import { AppLayoutHeaderSearch } from './AppLayoutHeaderSearch';
import { AppLayoutSubHeader } from './AppLayoutSubHeader';
import { ExitShoppingFlowModal } from './ExitShoppingFlowModal';

type Props = {
  withSearch: boolean;
  withMenuButton: boolean;
  withDonElenaMenuItems: boolean;
  headerContent?: ReactNode;
  subHeaderContent?: ReactNode;
  isDesktop: boolean;
  isDon: boolean;
  isShoppingFlow?: boolean;
  analyticsPayload?: object;
  isDiscoveryShoppingPleaseRefactorAppLayoutHeader: boolean;
  discoveryShoppingProps?: DiscoveryShoppingProps;
};

// This component needs to be refactored outside of AppLayout, the issue is AppLayout defines the header inside of it rather than per page
// If each page would declare it's own Header this hack wouldn't need to exist.
// https://linear.app/myzorro/issue/ENG-914/applayoutheader-refactor
export const AppLayoutHeader = ({
  withDonElenaMenuItems,
  withMenuButton,
  headerContent,
  subHeaderContent,
  withSearch,
  isDesktop,
  isDon,
  isShoppingFlow = false,
  analyticsPayload,
  isDiscoveryShoppingPleaseRefactorAppLayoutHeader,
  discoveryShoppingProps,
}: Props) => {
  const { sendEvent } = useAnalytics();
  const { isMobile } = useMobileDetector();
  const { navigateToRoute } = useDynamicRouter(COMMON_DYNAMIC_ROUTES);
  const { isImpersonated, user, isLoading } = useImpersonation();
  const { isSidebarOpen, toggleSidebar } = useAppLayout();
  const [isExitModalOpen, { open: openExitModal, close: closeExitModal }] =
    useDisclosure(false);

  let impersonatedFullName: string | undefined = undefined;
  if (isImpersonated) {
    if (!user || isLoading) {
      return null;
    }

    impersonatedFullName = getFullName(user);
  }

  const handleHelpClick = async () => {
    await showIntercom();
    await sendEvent('click_help_button', analyticsPayload);
  };

  const handleLogoClick = async () => {
    if (isShoppingFlow) {
      await sendEvent('zorro_logo_click_shopping', analyticsPayload);
      openExitModal();
      return;
    }

    navigateToRoute('HOME', []);
  };

  const handleGoToDashboard = () => {
    closeExitModal();
    navigateToRoute('HOME', []);
  };

  const { logoHeight, paddingBlock } =
    isDiscoveryShoppingPleaseRefactorAppLayoutHeader
      ? AppLayoutHeaderSizes.discoveryShopping
      : AppLayoutHeaderSizes.default;
  const headerPaddingInline = isDiscoveryShoppingPleaseRefactorAppLayoutHeader
    ? { base: 'md', xs: 'var(--space-32)' }
    : { base: 'md', xs: 'sm' };
  const contentPaddingInline = isDiscoveryShoppingPleaseRefactorAppLayoutHeader
    ? 0
    : 'sm';

  if (isMobile && isDiscoveryShoppingPleaseRefactorAppLayoutHeader) {
    return (
      <AppLayoutHeaderDiscoveryShopping
        discoveryShoppingProps={discoveryShoppingProps}
        analyticsPayload={analyticsPayload}
        onGoToDashboard={handleGoToDashboard}
        subHeaderContent={subHeaderContent}
        isExitModalOpen={isExitModalOpen}
        openExitModal={openExitModal}
        onContinue={closeExitModal}
      />
    );
  }

  return (
    <>
      <ExitShoppingFlowModal
        isOpen={isExitModalOpen}
        onContinue={closeExitModal}
        onGoToDashboard={handleGoToDashboard}
      />
      <AppShell.Header
        bg="transparent"
        zIndex={AppLayoutConstants.HEADER_ZINDEX}
        mih="fit-content"
      >
        {impersonatedFullName && (
          <AppLayoutHeaderImpersonation fullName={impersonatedFullName} />
        )}
        <Group
          py={paddingBlock}
          px={headerPaddingInline}
          gap="md"
          wrap="nowrap"
        >
          <Group
            pos="relative"
            pl={contentPaddingInline}
            gap={isDesktop ? 'lg' : 'xs'}
          >
            {withMenuButton && (
              <ActionIcon
                onClick={() => toggleSidebar()}
                data-open={isSidebarOpen}
                classNames={classNames}
                size="3.1rem"
              >
                <Icon icon={IconMenu2} />
              </ActionIcon>
            )}
            <Center
              onClick={handleLogoClick}
              style={{ cursor: 'pointer' }}
              h={logoHeight}
            >
              <ZorroLogo />
            </Center>
            {withSearch && <AppLayoutHeaderSearch />}
          </Group>
          <Group gap="0.5rem" ml="auto" pr={contentPaddingInline}>
            {headerContent}
            <Button
              onClick={handleHelpClick}
              variant="tertiary"
              size={isDesktop ? 'md' : 'sm'}
              leftSection={
                <Icon
                  icon={IconHelp}
                  color={brand.zorroGray400}
                  size={20}
                  style={{ marginRight: isDesktop ? 8 : 0 }}
                />
              }
            >
              {isDesktop && 'Help'}
            </Button>
            <AppLayoutHeaderMenu
              withDonElenaMenuItems={withDonElenaMenuItems}
              isDon={isDon}
              isDiscoveryShopping={
                isDiscoveryShoppingPleaseRefactorAppLayoutHeader
              }
            />
          </Group>
        </Group>
        {subHeaderContent && (
          <AppLayoutSubHeader>{subHeaderContent}</AppLayoutSubHeader>
        )}
      </AppShell.Header>
    </>
  );
};
